// react and redux hooks

import { useSelector, useDispatch } from "react-redux";
import { useEffect, useMemo } from "react";

// dayjs
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

// redux actions
import { fetchNotifications, deleteNotification, markNotificationAsRead } from "../../features/notifications/notificationsSlice";

// styles and icons
import { EditFormDiv, Div, Image, NotificationsTitle, Span } from "../../styles/styles";
import closeIcon from "../../images/close-icon.svg";

// utils
import { resolveEventTypeImageSrc, getTypeEvent } from "../../utils/getEventTypeImage";
import { selectEventTypes } from "../../features/eventTypes/eventTypesSlice";
import { getEventTypeLabel } from "../../utils/i18nDisplay";

//translations
import { useTranslation } from "react-i18next";
import { ensureNamespaces } from "../../i18n";


dayjs.extend(isSameOrBefore);

const NotificationsList = () => {

  const dispatch = useDispatch();
  const { list: notifications } = useSelector(s => s.notifications);
  const { currentUser } = useSelector(s => s.users);
  const globalEventTypes = useSelector(selectEventTypes);

  const { t } = useTranslation('account');
  const { t: tEvents } = useTranslation('events');
  useEffect(() => { 
    ensureNamespaces(['account', 'events']); 
  }, []);

  useEffect(() => {
    if (currentUser?.id) {
      dispatch(fetchNotifications(currentUser.id));
    }
  }, [dispatch, currentUser]);

  const today = dayjs().startOf("day");

  const sortedNotifications = useMemo(() => {
    const filtered = notifications.filter((n) => {
      const date = dayjs(n.notificationDate);
      return date.isValid() && date.isSameOrBefore(today, "day");
    });
    return [...filtered].sort((a, b) => dayjs(b.notificationDate) - dayjs(a.notificationDate));
  }, [notifications, today]);

  // mark-as-read при размонтировании (закрытие сайдбара/экрана)
  useEffect(() => {
    return () => {
      if (!currentUser?.id) return;
      const unreadIds = sortedNotifications.filter(n => !n.read).map(n => n.id);
      unreadIds.forEach(id => {
        dispatch(markNotificationAsRead({ userId: currentUser.id, notificationId: id }));
      });
    };
  }, [sortedNotifications, currentUser, dispatch]);

  const handleDelete = (id) => {
    if (!currentUser?.id) return;
    dispatch(deleteNotification({ userId: currentUser.id, notificationId: id }));
  };

  const formatDate = (isoString) => dayjs(isoString).format("DD.MM.YYYY");

  // Helper function to get translated notification message
  const getNotificationMessage = (notification) => {
    // Use translated message if messageKey exists, otherwise fallback to message
    if (notification.messageKey && notification.messageParams) {
      const translatedEventType = getEventTypeLabel(notification.messageParams.eventType, tEvents);
      const params = {
        eventType: translatedEventType,
        petName: notification.messageParams.petName
      };
      // messageKey should be like "notifications.remindMessage" and we're in 'account' namespace
      const translated = t(notification.messageKey, params);
      // If interpolation didn't work (still contains {{}}), do manual replacement as fallback
      if (translated && translated.includes('{{')) {
        return translated
          .replace(/\{\{eventType\}\}/g, translatedEventType)
          .replace(/\{\{petName\}\}/g, notification.messageParams.petName);
      }
      return translated;
    }
    return notification.message;
  };

  if (sortedNotifications.length === 0) {
    return <Div padding="20px" justifyStart>{t('notifications.message')}</Div>;
  }

  return (
    <Div column gap="20px" padding="20px" justifyStart>
      {sortedNotifications.map((n) => {
        const isToday = dayjs(n.notificationDate).isSame(today, "day");
        const isUnread = !n.read;

        return (
          <EditFormDiv key={n.id} row justify="space-between" align="center" height="auto">
            <Image
              src={closeIcon}
              onClick={() => handleDelete(n.id)}
              style={{ cursor: "pointer" }}
              imgHeight="12px"
            />
            <Div column gap="5px" mobileAlignStart alignStart>
              {isToday && (
                <NotificationsTitle
                  red={n.notificationType === "repeat"}
                  green={n.notificationType !== "repeat"}
                  style={{ opacity: isUnread ? 1 : 0.8 }}
                >
                  {n.notificationType === "repeat" ? (t('notifications.today') + " - " + formatDate(n.notificationDate)) : t('notifications.reminder')}
                </NotificationsTitle>
              )}
              <Span style={{ opacity: isUnread ? 1 : 0.8 }}>
                {getNotificationMessage(n)} - {formatDate(n.eventDate)}
              </Span>
            </Div>
            <Image
              src={resolveEventTypeImageSrc(n.eventType, globalEventTypes)}
              fallbackSrc={getTypeEvent(n.eventType)}
              imgHeight="36px"
              alt={n.eventType || ""}
            />
          </EditFormDiv>
        );
      })}
    </Div>
  );
};

export default NotificationsList;
